import { useToast } from "@chakra-ui/react";
import { PathStyleExtension } from "@deck.gl/extensions";
import { IconLayer, PathLayer, PathLayerProps } from "@deck.gl/layers";
import area from "@turf/area";
import { useCallback, useContext, useMemo, useState } from "react";

import { XYLocation } from "@nens/deck-gl";
import { doesNewPointCrossExistingLines } from "@nens/shared-utils";

import { ProjectConfigurationContext } from "@/pages/main-page";
import { useStatusQuery } from "@/queries/query-status";
import { getFlippedPolygonLayer } from "@/utils/get-flipped-polygon-layer";

import { COLORS } from "../consts/map-color-consts";
import { DEFAULT_ICON_LAYER_PROPS, DEFAULT_PATH_LAYER_PROPS } from "../consts/map-draw-consts";

export interface DrawPolygonLayersProps {
  confirmDrawingCallback: () => void;
  removeLastPointCallback: () => void;
  points: XYLocation[];
  dashDisabled: boolean;
  mouseLocation: XYLocation;
}

const useDrawPolygonLayers = ({
  points = [],
  dashDisabled,
  confirmDrawingCallback,
  removeLastPointCallback,
  mouseLocation,
}: DrawPolygonLayersProps) => {
  const basicPaths = useMemo(() => {
    return [
      new PathLayer({
        ...DEFAULT_PATH_LAYER_PROPS,
        id: "POLYGON-path-polygon-layer",
        data: [points],
        getColor: COLORS.MAP_LINE,
      } as PathLayerProps),

      new IconLayer({
        ...DEFAULT_ICON_LAYER_PROPS,
        id: "POLYGON-confirm-polygon-layer",
        data: [points[0]],
        getIcon: () => "confirmNoAnchor",
        onClick: () => {
          confirmDrawingCallback();
          return true;
        },
        visible: points.length > 2,
      }),
      new IconLayer({
        ...DEFAULT_ICON_LAYER_PROPS,
        id: "POLYGON-cancel-polygon-layer",
        data: [points[points.length - 1]],
        getIcon: () => "cancelNoAnchor",
        onClick: () => {
          removeLastPointCallback();
          return true;
        },
      }),
    ];
  }, [confirmDrawingCallback, points, removeLastPointCallback]);

  const dashPath = useMemo(() => {
    const dashLine =
      mouseLocation && points.length > 0 ? [points[points.length - 1], mouseLocation] : null;
    return [
      new PathLayer({
        ...DEFAULT_PATH_LAYER_PROPS,
        id: "POLYGON-dash-polygon-layer",
        data: dashLine ? [dashLine] : [],
        getColor: () => (!dashDisabled ? COLORS.MAP_LINE : COLORS.RED),
        getDashArray: [10, 10],
        dashJustified: true,
        extensions: [new PathStyleExtension({ dash: true })],
      } as PathLayerProps),
    ];
  }, [dashDisabled, mouseLocation, points]);

  return [...dashPath, ...basicPaths];
};

export interface UseDrawPolygonProps {
  mouseLocation: XYLocation;
}

export const useDrawPolygon = ({ mouseLocation }: UseDrawPolygonProps) => {
  const toast = useToast();
  const { location, setPolygon: setProjectPolygon } = useContext(ProjectConfigurationContext);
  const statusResponse = useStatusQuery();
  const maxSquareKilometers =
    (statusResponse.data?.authenticated && statusResponse.data?.maxSquareKilometers) || 4;

  const projectPolygon = location?.polygon ?? [];
  const projectHasPolygon = !!projectPolygon.length;

  const [polygon, setPolygon] = useState(projectPolygon);
  if (projectHasPolygon && polygon.length) {
    setPolygon([]);
  }

  const areaExceedsLimit = (() => {
    if (!polygon) return false;
    const polygonAreaSqMeters = area({
      type: "Polygon",
      coordinates: [[...polygon, mouseLocation, polygon[0]]],
    });
    const polygonAreaSqKm = polygonAreaSqMeters / 1e6;
    return polygonAreaSqKm > maxSquareKilometers;
  })();

  const newLineIntersects = mouseLocation
    ? doesNewPointCrossExistingLines(mouseLocation, polygon)
    : false;

  const onMapClick = () => {
    if (newLineIntersects)
      return toast({
        title: "Error",
        description: "Het nieuwe punt kruist een bestaande lijn in het polygoon.",
        status: "error",
      });
    if (areaExceedsLimit)
      return toast({
        title: "Error",
        description: `Het nieuwe punt overschrijdt de maximale oppervlakte van ${maxSquareKilometers} vierkante kilometer.`,
        status: "error",
      });
    setPolygon([...(polygon ?? []), mouseLocation]);
  };

  const confirmDrawingCallback = useCallback(() => {
    if (doesNewPointCrossExistingLines(mouseLocation, [...polygon].splice(1))) return;
    setProjectPolygon(polygon);
    setPolygon([]);
  }, [mouseLocation, polygon, setProjectPolygon]);

  const removeLastPointCallback = useCallback(() => {
    setPolygon(polygon.slice(0, -1));
  }, [polygon]);

  const layer = useDrawPolygonLayers({
    points: polygon,
    confirmDrawingCallback,
    removeLastPointCallback,
    dashDisabled: newLineIntersects || areaExceedsLimit,
    mouseLocation,
  });

  if (projectHasPolygon) {
    return {
      layer: getFlippedPolygonLayer(projectPolygon),
      onMapClick: undefined,
    };
  }

  return {
    layer,
    onMapClick,
  };
};
